/*
 * Obermaier — bath-models.js
 * <om-bath-models> shows the detail panel of the model chosen in the bathtub configurator
 * (<obermaier-badewannen>, live bundle, unchanged). It watches the configurator's own model buttons
 * ([data-model][aria-pressed] in its shadow root) and, for links with ?modell=<id>, opens that model
 * in the configurator the same way the cinema gallery does (assets/obermaier-badewannen-cinema.js).
 * The consultation form on the page gets the chosen model in a hidden field.
 */
import { define } from '@theme/core';

const ALIASES = { 'capri-corner': 'capri', 'royal-oyster': 'royal-oyster' };
const normalize = (value) => {
  const key = String(value || '').trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-');
  return ALIASES[key] || key;
};
const smooth = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth');

class OmBathModels extends HTMLElement {
  connectedCallback() {
    this.panels = [...this.querySelectorAll('[data-bm-model]')];
    this.extras = [...this.querySelectorAll('[data-bm-extra]')];
    if (!this.panels.length) return;
    this.classList.add('is-enhanced');

    const wanted = normalize(new URLSearchParams(location.search).get('modell'));
    const extra = this.extras.find((el) => el.dataset.bmExtra === wanted);
    const panel = this.panels.find((el) => el.dataset.bmModel === wanted);
    this.show(panel ? wanted : this.panels[0].dataset.bmModel);

    if (extra) {
      requestAnimationFrame(() => extra.scrollIntoView({ behavior: smooth(), block: 'start' }));
      // A model without configurator keeps the form until the visitor picks a model in the configurator.
      this.formModel = extra.dataset.bmName;
      this.setFormModel(this.formModel);
    } else if (panel) {
      document.getElementById('wannenmodelle')?.scrollIntoView({ behavior: smooth(), block: 'start' });
      this.pending = wanted;
      this.open(wanted);
    }

    customElements.whenDefined('obermaier-badewannen').then(() => this.follow());
    customElements.whenDefined('obermaier-bath-cinema').then(() => this.connectGallery());

    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-bm-thumb]');
      if (thumb) this.swapImage(thumb);
      const consult = event.target.closest('[data-bm-consult]');
      if (consult) {
        const name = consult.closest('[data-bm-name]')?.dataset.bmName;
        if (name) {
          this.formModel = name;
          this.setFormModel(name);
        }
      }
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.opener?.disconnect();
    clearTimeout(this.openTimer);
    this.galleryAbort?.abort();
    clearTimeout(this.galleryScrollTimer);
  }

  connectGallery() {
    if (!this.isConnected) return;
    const gallery = document.querySelector('obermaier-bath-cinema');
    const rail = gallery?.querySelector('.bath-model-rail');
    const cards = [...(rail?.querySelectorAll('.bath-model-card') || [])];
    const buttons = [...(gallery?.querySelectorAll('[data-collection-index]') || [])];
    if (!rail || !cards.length || cards.length !== buttons.length) return;
    this.galleryAbort?.abort();
    this.galleryAbort = new AbortController();
    const { signal } = this.galleryAbort;
    gallery.addEventListener('click', (event) => {
      if (event.target.closest('[data-rail-prev], [data-rail-next], [data-collection-index]')) clearTimeout(this.galleryScrollTimer);
    }, { capture: true, signal });
    rail.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      clearTimeout(this.galleryScrollTimer);
      const index = buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
      cards[index]?.focus({ preventScroll: true });
    }, { signal });
    const sync = () => {
      clearTimeout(this.galleryScrollTimer);
      const max = rail.scrollWidth - rail.clientWidth;
      let closest = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const target = Math.max(0, Math.min(max, card.offsetLeft - cards[0].offsetLeft));
        const delta = Math.abs(rail.scrollLeft - target);
        if (delta < distance) { closest = index; distance = delta; }
      });
      if (buttons[closest].getAttribute('aria-pressed') !== 'true') buttons[closest].click();
    };
    rail.addEventListener('scroll', () => {
      clearTimeout(this.galleryScrollTimer);
      this.galleryScrollTimer = setTimeout(sync, 120);
    }, { passive: true, signal });
  }

  /** Mirror the configurator: whenever its pressed model changes, show that panel. */
  follow() {
    const root = document.querySelector('obermaier-badewannen')?.shadowRoot;
    if (!root) return;
    const sync = () => {
      const pressed = root.querySelector('[data-model][aria-pressed="true"]');
      // While a linked model is still being opened, ignore the configurator's start model.
      if (!pressed || (this.pending && pressed.dataset.model !== this.pending)) return;
      this.pending = null;
      if (this.lastPressed && this.lastPressed !== pressed.dataset.model) this.formModel = null;
      this.lastPressed = pressed.dataset.model;
      this.show(pressed.dataset.model);
    };
    this.observer = new MutationObserver(sync);
    this.observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
    sync();
  }

  /** Open a model in the configurator once its buttons are ready (as the cinema gallery does). */
  open(id) {
    customElements.whenDefined('obermaier-badewannen').then(() => {
      const root = document.querySelector('obermaier-badewannen')?.shadowRoot;
      if (!root) return;
      const attempt = () => {
        const button = [...root.querySelectorAll('[data-model]')].find((el) => el.dataset.model === id);
        if (!button || button.disabled || root.querySelector('#loading')?.hidden === false) return;
        if (button.getAttribute('aria-pressed') !== 'true') button.click();
        else this.pending = null;
        this.opener?.disconnect();
        clearTimeout(this.openTimer);
      };
      this.opener = new MutationObserver(attempt);
      this.opener.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'aria-pressed'] });
      this.openTimer = setTimeout(() => {
        this.opener?.disconnect();
        this.pending = null;
      }, 20000);
      attempt();
    });
  }

  show(id) {
    let active = null;
    for (const panel of this.panels) {
      const on = panel.dataset.bmModel === id;
      panel.classList.toggle('is-active', on);
      if (on) active = panel;
    }
    if (!active) {
      this.panels[0].classList.add('is-active');
      active = this.panels[0];
    }
    if (!this.formModel) this.setFormModel(active.dataset.bmName);
  }

  swapImage(thumb) {
    const panel = thumb.closest('[data-bm-model], [data-bm-extra]');
    const img = panel?.querySelector('[data-bm-stage] img');
    if (!img) return;
    img.srcset = thumb.dataset.srcset || '';
    img.src = thumb.dataset.src;
    panel.querySelectorAll('[data-bm-thumb]').forEach((el) => el.toggleAttribute('aria-current', el === thumb));
    thumb.setAttribute('aria-current', 'true');
  }

  setFormModel(name) {
    document.querySelectorAll('[data-bath-model-input]').forEach((input) => (input.value = name || ''));
  }
}

define('om-bath-models', OmBathModels);

